import { sortBy } from 'lodash'
import { z } from 'zod'
import {
  onDemandCourseProperties,
  learningItemProperties,
  scheduledCourseEventProperties,
  scheduledCourseProperties,
  learningPathProperties,
} from './BaseModels'
import { dummyLearningItem } from './LearningItem'
import parseModel from './parseModel'
import { learningItemPermissionsParser } from './Permissions'

/**********************************************************************
 * baseLearningItemProperties
 **********************************************************************/
const baseLearningItemProperties = {
  ...learningItemProperties,
  permissions: learningItemPermissionsParser,
}

/**********************************************************************
 * OnDemandCourse
 **********************************************************************/
export const onDemandCourseParser = z.object({
  ...baseLearningItemProperties,
  kind: z.literal('onDemandCourse'),
  onDemandCourse: z.object(onDemandCourseProperties),
})

export type OnDemandCourse = z.infer<typeof onDemandCourseParser>

/**********************************************************************
 * LearningPath
 **********************************************************************/
export const learningPathParser = z.object({
  ...baseLearningItemProperties,
  kind: z.literal('learningPath'),
  learningPath: z.object({
    ...learningPathProperties,
  }),
})

export type LearningPath = z.infer<typeof learningPathParser>

/**********************************************************************
 * ScheduledCourse
 **********************************************************************/
export const scheduledCourseParser = z.object({
  ...baseLearningItemProperties,
  kind: z.literal('scheduledCourse'),
  scheduledCourse: z.object({
    ...scheduledCourseProperties,
    events: z.array(z.object(scheduledCourseEventProperties)).transform((e) => sortBy(e, 'startDate')),
  }),
})

export type ScheduledCourse = z.infer<typeof scheduledCourseParser>

/**********************************************************************
 * LearningItem
 **********************************************************************/
export const anonymousLearningItemParser = z.preprocess(
  (o: any) => {
    if (o) {
      o.kind = o?.onDemandCourseUuid ? 'onDemandCourse' : o?.scheduledCourseUuid ? 'scheduledCourse' : 'learningPath'
    }
    return o
  },
  z.discriminatedUnion('kind', [onDemandCourseParser, learningPathParser, scheduledCourseParser])
)

export type AnonymousLearningItem = z.infer<typeof anonymousLearningItemParser>

export const parseAnonymousLearningItem = parseModel<AnonymousLearningItem>(
  anonymousLearningItemParser,
  dummyLearningItem
)
